import React, { useState, useEffect } from "react";
import axios from "axios";
import PrescriptionHistory from "./PrescriptionHistory";
import Navbar from "./Navbar";
import { useNavigate, useLocation } from "react-router-dom";
import { Loader2, AlertTriangle, FileText } from "lucide-react";

const PrescriptionDashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [prescriptions, setPrescriptions] = useState([]);
  const [filteredPrescriptions, setFilteredPrescriptions] = useState([]);
  const [selectedDate, setSelectedDate] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchPrescriptions = async () => {
    const patient_id = location.state?.patient_id;
    if (!patient_id) {
      setError("Patient ID is missing.");
      setIsLoading(false);
      return;
    }

    try {
      const response = await axios.post(
        "https://www.careplus.gopillz.com/prescriptions",
        JSON.stringify({ patient_id }),
        {
          headers: {
            Accept: "*/*",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.status === 200 && response.data.data) {
        setPrescriptions(response.data.data);
        setFilteredPrescriptions(response.data.data);
      } else {
        setError("No prescriptions found.");
      }
    } catch (err) {
      console.error("Error fetching prescriptions:", err);
      setError("Failed to fetch prescriptions. Please try again later.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchPrescriptions();
  }, []);

  const handleBack = () => navigate(-1);

  const handleDateChange = (e) => {
    const date = e.target.value;
    setSelectedDate(date);
    if (!date) {
      setFilteredPrescriptions(prescriptions);
      return;
    }
    const filtered = prescriptions.filter((p) => p.date.startsWith(date));
    setFilteredPrescriptions(filtered);
  };

  return (
    <div className="min-h-screen bg-gradient-to-tr from-emerald-50 to-white">
      <Navbar />

      <div className="pt-28 px-4 sm:px-8 max-w-6xl mx-auto">
        <div className="text-center mb-10">
          <h1 className="text-3xl sm:text-4xl font-bold text-emerald-800">
            🧾 Child’s Prescription History
          </h1>
          <p className="text-gray-600 mt-2 text-sm">
            View and manage your child’s past medical prescriptions.
          </p>
        </div>

        {/* Date Filter */}
        <div className="flex justify-center mb-6">
          <input
            type="date"
            value={selectedDate}
            onChange={handleDateChange}
            className="border border-emerald-300 rounded-lg p-2 shadow-sm focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:border-transparent text-sm text-gray-700"
          />
        </div>

        {/* Content */}
        <div className="bg-white shadow-lg rounded-xl p-6">
          {isLoading ? (
            <div className="text-center text-emerald-600 flex justify-center items-center gap-2">
              <Loader2 className="animate-spin" />
              Loading prescriptions...
            </div>
          ) : error ? (
            <div className="text-center text-red-600 flex items-center justify-center gap-2 font-semibold">
              <AlertTriangle className="text-red-500" />
              {error}
            </div>
          ) : filteredPrescriptions.length === 0 ? (
            <div className="text-center text-gray-500 flex items-center justify-center gap-2">
              <FileText className="text-gray-400" />
              No prescriptions available for the selected date.
            </div>
          ) : (
            <PrescriptionHistory
              prescriptions={filteredPrescriptions.slice(0, 1)}
              selectedDate={selectedDate}
              onDateChange={handleDateChange}
              onBackClick={handleBack}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default PrescriptionDashboard;
